import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { usaStates } from "typed-usa-states"; // Import the list of states
import { createUser } from "../services/userService";
import { useNavigate } from "react-router-dom";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

const Register: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);


  const stateAbbreviations = usaStates.reduce((acc, state) => {
    acc[state.name] = state.abbreviation.toLowerCase(); // Map state names to abbreviations
    return acc;
  }, {} as Record<string, string>);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!state || !company) {
      setError("Please select a state and enter your company name.");
      return;
    }

    try {
      // Generate client_id and client_name
      const stateAbbreviation = stateAbbreviations[state];
      const uniqueId = Math.floor(Math.random() * 1000);
      const client_id = `${stateAbbreviation}_${uniqueId}`;
      const client_name = `${company} (${state})`;

      // Call the createUser function with the additional fields
      const data = await createUser(username, password, client_id, client_name, email);

      localStorage.setItem("client_id", client_id);

      if (data.job_id) {
        localStorage.setItem("job_id", data.job_id);
      } else {
        localStorage.setItem("job_id", "");
      }

      // Clear the form and navigate to home
      setUsername("");
      setPassword("");
      setState("");
      setCompany("");
      setEmail("");
      setSuccessMessage("You're Registered!");
      setSuccessOpen(true);
    } catch (err: any) {
      setError(err.message || "An error occurred. Please try again.");
    }
  };

  const fieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F6FFE8",
    },
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#D9D9D91A",
      }}
    >
      <Box
        sx={{
          width: 400,
          p: 4,
          border: "1px solid #ccc",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          backgroundColor: "#fff",
          mt: -25,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Register
        </Typography>
        <Dialog
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          slotProps={{
            paper: {
              sx: { width: 480, maxWidth: "90vw" },
            },
          }}
        >
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 2, px: 4 }}>
              <Typography variant="h6" sx={{ textAlign: "center" }}>{successMessage}</Typography>
              <Box component="img" src="/check-icon.png" alt="Success" sx={{ width: 64, height: 64 }} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={() => {
                setSuccessOpen(false);
                navigate("/home");
              }}
              autoFocus
              variant="contained"
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <TextField
            label="Company Name"
            variant="outlined"
            fullWidth
            sx={fieldSx}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
          <TextField
            label="Company Email Address"
            variant="outlined"
            fullWidth
            sx={fieldSx}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel id="state-label">State</InputLabel>
            <Select
              labelId="state-label"
              value={state}
              onChange={(e) => setState(e.target.value)}
              label="State"
            >
              {usaStates.map((state) => (
                <MenuItem key={state.name} value={state.name}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Username"
            variant="outlined"
            fullWidth
            sx={fieldSx}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            sx={fieldSx}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <Typography color="error" sx={fieldSx}>
              {error}
            </Typography>
          )}
          <Button 
            type="submit" 
            variant="contained" 
            fullWidth
            sx={{ backgroundColor: "#0373B5" }}
          >
            Register
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default Register;